import { Helmet } from "react-helmet-async";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyPhone from "@/components/StickyPhone";

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mb-10">
    <h2 className="text-xl font-heading font-bold text-foreground mb-3">{title}</h2>
    <div className="text-muted-foreground space-y-3">{children}</div>
  </div>
);

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Terms of Service | Hill Brothers</title>
        <meta name="description" content="Read the Terms of Service for Hill Brothers Painting &amp; Contracting. Plain-English terms that are fair to you and protect us both." />
        <meta property="og:title" content="Terms of Service | Hill Brothers" />
        <meta property="og:description" content="Plain-English terms that are fair to you and protect us both." />
        <meta property="og:url" content="https://hillbrothersnh.com/terms" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Terms of Service | Hill Brothers" />
        <meta name="twitter:description" content="Plain-English terms of service." />
        <link rel="canonical" href="https://hillbrothersnh.com/terms" />
        <script type="application/ld+json">{JSON.stringify({
          "@context":"https://schema.org","@type":"BreadcrumbList",
          itemListElement:[
            {"@type":"ListItem",position:1,name:"Home",item:"https://hillbrothersnh.com/"},
            {"@type":"ListItem",position:2,name:"Terms of Service",item:"https://hillbrothersnh.com/terms"}
          ]
        })}</script>
      </Helmet>

      <Header />

      <main className="pt-20">
        <section className="bg-primary py-16">
          <div className="section-container">
            <Link to="/" className="inline-flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary-foreground mb-4">
              Terms of Service
            </h1>
            <p className="text-xl text-primary-foreground/80 max-w-2xl">
              Plain-English terms that are fair to you and protect us both.
            </p>
          </div>
        </section>

        <section className="py-16">
          <div className="section-container max-w-3xl">

            <p className="text-sm text-muted-foreground mb-10">
              <strong>Effective Date:</strong> February 2026 &nbsp;|&nbsp; <strong>Last Updated:</strong> February 18, 2026
            </p>

            <Section title="1. Welcome">
              <p>These terms govern your use of the Hill Brothers Painting & Contracting website ("hillbrothersnh.com" and all associated pages). By using this site, you agree to these terms. If you don't agree, please don't use the site.</p>
            </Section>

            <Section title="2. Use in Good Faith">
              <p>We built this website to connect with customers and potential team members. Please use it in good faith: submit accurate information, don't misuse our forms, and don't attempt to interfere with or exploit the site or its systems.</p>
              <p>We reserve the right to block, ignore, or report any submissions or activity that appears fraudulent, abusive, automated (bots/spam), or made in bad faith.</p>
            </Section>

            <Section title="3. Accuracy of Information">
              <p>When you submit a contact form or job application, you agree that the information you provide is truthful and accurate to the best of your knowledge. We rely on this information to respond to your inquiry or evaluate your application.</p>
              <p>Submitting false, misleading, or someone else's personal information is prohibited and may result in legal action.</p>
            </Section>

            <Section title="4. No Guarantees on Estimates or Timelines">
              <p>Any estimates, quotes, or timelines discussed through this site or during appointments are preliminary and non-binding until confirmed in a written contract. Actual project costs and schedules may vary based on site conditions, materials, scope changes, and other factors.</p>
            </Section>

            <Section title="5. Intellectual Property">
              <p>All content on this website, including photos, text, logos, and design elements, is the property of Hill Brothers Painting & Contracting LLC. You may not copy, reproduce, distribute, or use our content without written permission.</p>
              <p>Photos of our work may not be used to misrepresent another company's capabilities or portfolio.</p>
            </Section>

            <Section title="6. Privacy & Data Protection">
              <p>We take your privacy seriously. All personal data submitted through our forms is encrypted at rest using AES-256-GCM encryption. We never sell or share your data. For full details, please read our <Link to="/privacy" className="text-primary underline hover:no-underline font-medium">Privacy Policy</Link>.</p>
            </Section>

            <Section title="7. Communications">
              <p>By submitting a contact form, you consent to receiving an email response from us. Phone or text communication requires your explicit consent via the checkbox on the form.</p>
              <p>We will never send unsolicited marketing materials. All communications include an unsubscribe option.</p>
            </Section>

            <Section title="8. Limitation of Liability">
              <p>This website and its content are provided "as is." While we strive for accuracy and reliability, we make no warranties about the completeness, accuracy, or availability of the site.</p>
              <p>Hill Brothers Painting & Contracting LLC is not liable for any indirect, incidental, or consequential damages arising from your use of this website. Our total liability for any claim related to this site shall not exceed the amount you paid us (if any) in connection with the matter giving rise to the claim.</p>
            </Section>

            <Section title="9. Anti-Abuse & Anti-Fraud">
              <p>We actively monitor submissions for signs of spam, fraud, and abuse. Automated or bulk form submissions are prohibited. We reserve the right to take appropriate legal action against any individual or entity that attempts to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Submit fraudulent or malicious information</li>
                <li>Exploit vulnerabilities in our systems</li>
                <li>Impersonate another person or business</li>
                <li>Harass, threaten, or defame our company or team members</li>
                <li>Use our site for any unlawful purpose</li>
              </ul>
            </Section>

            <Section title="10. Right to Refuse Service">
              <p>Hill Brothers Painting & Contracting LLC reserves the right to decline any project, application, or inquiry at our sole discretion and for any lawful reason.</p>
            </Section>

            <Section title="11. Dispute Resolution">
              <p>If a dispute arises, we prefer to resolve it through good-faith conversation first. If we can't reach a resolution, any legal proceedings shall be governed by the laws of the State of New Hampshire and shall be brought in the courts of Hillsborough County, New Hampshire.</p>
            </Section>

            <Section title="12. Changes to These Terms">
              <p>We may update these terms from time to time. When we do, we'll update the "Last Updated" date at the top of this page. Continued use of the site after changes constitutes acceptance of the updated terms.</p>
            </Section>

            <div className="border-t border-border pt-8 mt-12">
              <h2 className="text-xl font-heading font-bold text-foreground mb-3">Questions?</h2>
              <p className="text-muted-foreground">
                If you have questions about these terms, call us at{" "}
                <a href="tel:+16034244608" className="text-primary underline hover:no-underline font-medium">603 424 4608</a> or{" "}
                <Link to="/#contact" className="text-primary underline hover:no-underline font-medium">send us a message</Link>.
              </p>
            </div>

          </div>
        </section>
      </main>

      <Footer />
      <StickyPhone />
    </div>
  );
}
